import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { 
  ArrowRight, 
  Shield, 
  Calculator, 
  Building2, 
  FileCheck, 
  CheckCircle,
  Users,
  Clock,
  MessageCircle,
  X,
  Bot,
  User,
  Mic,
  Lock,
  Eye,
  BookOpen,
} from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { Button } from '../components/ui/Button'
import { ChatInterface } from '../components/chat/ChatInterface'
import { EmailCaptureModal } from '../components/ui/EmailCaptureModal'
import { RegisterModal } from '../components/auth/RegisterModal'
import { useIsMobile } from '../hooks/useIsMobile'
import { PublicFooter } from '../components/layout/PublicFooter'
import { PublicNavigation } from '../components/layout/PublicNavigation'
import { Logo } from '../components/ui/Logo'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'

export const HomePage: React.FC = () => {
  const [showChat, setShowChat] = useState(false)
  const [showEmailModal, setShowEmailModal] = useState(false)
  const [showRegisterModal, setShowRegisterModal] = useState(false)
  const [capturedEmail, setCapturedEmail] = useState('')
  const isMobile = useIsMobile()
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()

  const handleStartProcess = () => {
    // Account creation happens through chat
  }

  const handleStartExchange = () => {
    if (isAuthenticated && user?.email) {
      setShowChat(true)
    } else {
      setShowEmailModal(true)
    }
  }

  const handleEmailCaptured = (email: string) => {
    // Email captured, store it and show registration modal
    setCapturedEmail(email)
    setShowEmailModal(false)
    // Small delay to ensure modal closes smoothly
    setTimeout(() => {
      setShowRegisterModal(true)
    }, 200)
  }

  const handleRegistrationSuccess = () => {
    // Registration successful, close modal and navigate to dashboard
    setShowRegisterModal(false)
    setTimeout(() => {
      navigate('/app')
    }, 300)
  }

  // Mobile: Full screen mode
  if (showChat && isMobile) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Button 
                variant="outline" 
                onClick={() => setShowChat(false)}
                className="mb-4"
              >
                ← Back to Home
              </Button>
              <h1 className="text-3xl font-bold text-center mb-2">1031 Exchange Assistant</h1>
              <p className="text-center text-muted-foreground">
                Get personalized guidance for your 1031 exchange
              </p>
            </div>
            <div className="h-[600px]">
              <ChatInterface 
                onStartProcess={handleStartProcess}
                className="h-full"
              />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 relative overflow-hidden transition-colors duration-300">

      <PublicNavigation onChatClick={handleStartExchange} />

      {/* Hero Section */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-32 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
              Automate Your{' '}
              <span className="text-[#69adba]">1031 Exchange</span>
            </h1>
            <p className="mt-4 sm:mt-6 text-base sm:text-lg leading-7 sm:leading-8 text-slate-600 dark:text-slate-400 max-w-3xl mx-auto px-4 sm:px-0">
              The Open1031 platform automates the full lifecycle of 1031 exchanges by combining 
              AI-driven workflows, expert domain logic, and a seamless user experience.
            </p>
            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 px-4 sm:px-0">
              <button 
                onClick={() => setShowChat(true)}
                className="w-full sm:w-auto flex items-center justify-center space-x-2 px-8 sm:px-12 py-4 sm:py-5 text-base sm:text-lg font-bold text-slate-900 dark:text-slate-100 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 border-2 border-slate-200 dark:border-slate-700 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200"
              >
                <BookOpen className="h-5 w-5" />
                <span>Learn More</span>
              </button>
              <button 
                onClick={handleStartExchange}
                className="w-full sm:w-auto flex items-center justify-center space-x-2 px-8 sm:px-12 py-4 sm:py-5 text-base sm:text-lg font-bold text-white bg-[#69adba] hover:bg-[#5a9aa6] rounded-xl shadow-2xl hover:shadow-[#69adba]/40 hover:scale-105 active:scale-95 transition-all duration-200"
              >
                <Building2 className="h-5 w-5" />
                <span>Start an Exchange</span>
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile App Download Section - Prominent */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-br from-[#69adba]/20 via-[#69adba]/10 to-slate-50 dark:from-[#69adba]/30 dark:via-[#69adba]/20 dark:to-slate-900 border-y border-[#69adba]/20 dark:border-[#69adba]/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8 sm:mb-12">
              <div className="inline-flex items-center space-x-2 bg-[#69adba]/20 dark:bg-[#69adba]/30 text-[#69adba] dark:text-[#69adba] px-4 py-2 rounded-full text-sm font-medium mb-4">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17 2H7c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 18H7V4h10v16zm-1-6h-3V8h3v6z"/>
                </svg>
                <span>Mobile App</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-100 mb-3 sm:mb-4 px-4 sm:px-0">
                Download the{' '}
                <span className="text-[#69adba]">Open1031 App</span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl text-slate-600 dark:text-slate-400 max-w-2xl mx-auto px-4 sm:px-0">
                Manage your 1031 exchange on the go. Upload documents, chat with Neela, track deadlines, and more—all from your phone.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start md:items-center">
              {/* Left side - QR Codes, Buttons, and Features */}
              <div className="space-y-6 sm:space-y-6 order-2 md:order-1">
                {/* QR Codes Section - Desktop Only */}
                <div className="hidden md:block text-center mb-6">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-4">Scan to download</p>
                  <div className="flex justify-center gap-4 sm:gap-6">
                    {/* Apple QR Code */}
                    <div className="text-center">
                      <div className="bg-white p-3 rounded-lg border border-slate-200 dark:border-slate-600 inline-block mb-2">
                        <QRCodeSVG
                          value="https://apps.apple.com/app/open1031"
                          size={120}
                          level="M"
                          includeMargin={false}
                          fgColor="#000000"
                          bgColor="#ffffff"
                        />
                      </div>
                      <p className="text-xs text-slate-600 dark:text-slate-400">App Store</p>
                    </div>
                    {/* Google Play QR Code */}
                    <div className="text-center">
                      <div className="bg-white p-3 rounded-lg border border-slate-200 dark:border-slate-600 inline-block mb-2">
                        <QRCodeSVG
                          value="https://play.google.com/store/apps/details?id=com.open1031.app"
                          size={120}
                          level="M"
                          includeMargin={false}
                          fgColor="#000000"
                          bgColor="#ffffff"
                        />
                      </div>
                      <p className="text-xs text-slate-600 dark:text-slate-400">Google Play</p>
                    </div>
                  </div>
                </div>

                {/* App Store Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  {/* Apple App Store */}
                  <a 
                    href="https://apps.apple.com/app/open1031" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-6 py-3 sm:px-4 sm:py-2.5 bg-black dark:bg-slate-800 text-white rounded-xl sm:rounded-lg hover:bg-slate-800 dark:hover:bg-slate-700 active:scale-95 transition-all group text-sm sm:text-sm shadow-lg sm:shadow-none"
                  >
                    <svg className="w-6 h-6 sm:w-5 sm:h-5 mr-3 sm:mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                    </svg>
                    <div className="text-left">
                      <div className="text-xs sm:text-[10px] opacity-80">Download on the</div>
                      <div className="text-base sm:text-sm font-semibold -mt-0.5">App Store</div>
                    </div>
                  </a>

                  {/* Google Play Store */}
                  <a 
                    href="https://play.google.com/store/apps/details?id=com.open1031.app" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-6 py-3 sm:px-4 sm:py-2.5 bg-black dark:bg-slate-800 text-white rounded-xl sm:rounded-lg hover:bg-slate-800 dark:hover:bg-slate-700 active:scale-95 transition-all group text-sm sm:text-sm shadow-lg sm:shadow-none"
                  >
                    <svg className="w-6 h-6 sm:w-5 sm:h-5 mr-3 sm:mr-2" viewBox="0 0 24 24">
                      <path fill="#EA4335" d="M3.609 1.814L13.792 12 3.609 22.186a1.979 1.979 0 01-.289-1.046V2.86c0-.384.11-.762.289-1.046z"/>
                      <path fill="#FBBC04" d="M13.792 12l3.332-3.332 3.758 2.133a1.978 1.978 0 010 3.398l-3.758 2.133z"/>
                      <path fill="#4285F4" d="M3.32.768l10.472 5.9L17.124 10l-3.332 3.332L3.32 23.232A1.998 1.998 0 012 21.14V2.86a1.998 1.998 0 011.32-1.892z"/>
                      <path fill="#34A853" d="M13.792 12L3.32 1.768a2.015 2.015 0 012.289 0l14.273 8.033z"/>
                    </svg>
                    <div className="text-left">
                      <div className="text-xs sm:text-[10px] opacity-80">GET IT ON</div>
                      <div className="text-base sm:text-sm font-semibold -mt-0.5">Google Play</div>
                    </div>
                  </a>
                </div>

                {/* Coming soon badge */}
                <div className="text-center">
                  <span className="inline-flex items-center px-5 py-2.5 sm:px-4 sm:py-2 bg-[#69adba]/10 dark:bg-[#69adba]/20 text-[#69adba] dark:text-[#69adba] rounded-full font-medium text-sm sm:text-sm">
                    Coming Q2 2025
                  </span>
                </div>

                {/* Features list */}
                <div className="pt-6 border-t border-slate-200 dark:border-slate-700">
                  <h3 className="text-lg sm:text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4 sm:mb-4 text-center">App Features</h3>
                  <ul className="space-y-3.5 sm:space-y-3">
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 sm:h-5 sm:w-5 text-[#69adba] mt-0.5 flex-shrink-0" />
                      <span className="text-base sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">Real-time notifications for important deadlines</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 sm:h-5 sm:w-5 text-[#69adba] mt-0.5 flex-shrink-0" />
                      <span className="text-base sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">Upload documents with your camera</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 sm:h-5 sm:w-5 text-[#69adba] mt-0.5 flex-shrink-0" />
                      <span className="text-base sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">AI assistant available 24/7</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <CheckCircle className="h-6 w-6 sm:h-5 sm:w-5 text-[#69adba] mt-0.5 flex-shrink-0" />
                      <span className="text-base sm:text-sm text-slate-700 dark:text-slate-300 leading-relaxed">Secure biometric authentication</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Right side - Phone Mockup */}
              <div className="flex justify-center order-1 md:order-2">
                <div className="relative">
                  {/* Phone frame */}
                  <div className="relative w-64 sm:w-72 h-[500px] sm:h-[600px] bg-slate-800 dark:bg-slate-700 rounded-[3rem] p-3 sm:p-4 shadow-2xl border border-slate-700 dark:border-slate-600">
                    {/* Notch */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-6 bg-slate-800 dark:bg-slate-700 rounded-b-3xl z-10"></div>
                    
                    {/* Screen */}
                    <div className="w-full h-full bg-white dark:bg-slate-900 rounded-[2.5rem] overflow-hidden flex flex-col border-2 border-slate-200 dark:border-slate-700">
                      {/* Chat Header */}
                      <div className="bg-gradient-to-r from-[#69adba] to-[#5a9aa6] text-white p-4 flex items-center space-x-3 shadow-sm">
                        <div className="flex-1">
                          <img 
                            src="/logo.png" 
                            alt="Open1031" 
                            className="h-8 w-auto brightness-0 invert"
                          />
                        </div>
                        <MessageCircle className="h-5 w-5" />
                      </div>

                      {/* Chat Messages */}
                      <div className="flex-1 p-4 space-y-3 overflow-hidden bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-800">
                        {/* AI Message 1 */}
                        <div className="flex items-start space-x-2">
                          <div className="w-8 h-8 rounded-full bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center flex-shrink-0 ring-1 ring-[#69adba]/20 dark:ring-[#69adba]/30">
                            <Bot className="h-4 w-4 text-[#69adba]" />
                          </div>
                          <div className="bg-white dark:bg-slate-800 rounded-2xl rounded-tl-none px-4 py-2 max-w-[80%] border border-slate-200 dark:border-slate-700 shadow-sm">
                            <p className="text-xs text-slate-900 dark:text-slate-100 leading-relaxed">
                              Hi! I'm Neela, your 1031 exchange assistant. How can I help you today?
                            </p>
                          </div>
                        </div>

                        {/* User Message */}
                        <div className="flex items-start space-x-2 justify-end">
                          <div className="bg-gradient-to-r from-[#69adba] to-[#5a9aa6] rounded-2xl rounded-tr-none px-4 py-2 max-w-[80%] shadow-sm">
                            <p className="text-xs text-white leading-relaxed">
                              I need to start a 1031 exchange
                            </p>
                          </div>
                          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-[#69adba] to-[#5a9aa6] flex items-center justify-center flex-shrink-0 ring-2 ring-[#69adba]/20 dark:ring-[#69adba]/30">
                            <User className="h-4 w-4 text-white" />
                          </div>
                        </div>

                        {/* AI Message 2 */}
                        <div className="flex items-start space-x-2">
                          <div className="w-8 h-8 rounded-full bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center flex-shrink-0 ring-1 ring-[#69adba]/20 dark:ring-[#69adba]/30">
                            <Bot className="h-4 w-4 text-[#69adba]" />
                          </div>
                          <div className="bg-white dark:bg-slate-800 rounded-2xl rounded-tl-none px-4 py-2 max-w-[80%] border border-slate-200 dark:border-slate-700 shadow-sm">
                            <p className="text-xs text-slate-900 dark:text-slate-100 leading-relaxed">
                              Great! Let's get started. First, tell me about the property you're selling...
                            </p>
                          </div>
                        </div>

                        {/* Typing indicator */}
                        <div className="flex items-start space-x-2">
                          <div className="w-8 h-8 rounded-full bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center flex-shrink-0 ring-1 ring-[#69adba]/20 dark:ring-[#69adba]/30">
                            <Bot className="h-4 w-4 text-[#69adba]" />
                          </div>
                          <div className="bg-white dark:bg-slate-800 rounded-2xl rounded-tl-none px-4 py-3 border border-slate-200 dark:border-slate-700 shadow-sm">
                            <div className="flex space-x-1.5">
                              <div className="w-2 h-2 bg-[#69adba] dark:bg-[#69adba] rounded-full animate-bounce opacity-60" style={{ animationDelay: '0ms' }}></div>
                              <div className="w-2 h-2 bg-[#69adba] dark:bg-[#69adba] rounded-full animate-bounce opacity-60" style={{ animationDelay: '150ms' }}></div>
                              <div className="w-2 h-2 bg-[#69adba] dark:bg-[#69adba] rounded-full animate-bounce opacity-60" style={{ animationDelay: '300ms' }}></div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Chat Input */}
                      <div className="p-3 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-inner">
                        <div className="flex items-center space-x-2 bg-slate-100 dark:bg-slate-800 rounded-full px-4 py-2.5 border border-slate-200 dark:border-slate-700 shadow-sm">
                          <MessageCircle className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                          <div className="flex-1 text-xs text-slate-500 dark:text-slate-400">Type a message...</div>
                          <Mic className="h-4 w-4 text-[#69adba]" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-12 sm:py-16 md:py-20 relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
              Complete 1031 Exchange Solution
            </h2>
            <p className="mt-3 sm:mt-4 text-base sm:text-lg text-slate-600 dark:text-slate-400 px-4 sm:px-0">
              Everything you need for a successful 1031 exchange in one platform
            </p>
          </div>
          
          <div className="mt-10 sm:mt-12 md:mt-16 grid grid-cols-1 gap-5 sm:gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3">
            <div className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-xl p-5 sm:p-6 rounded-xl sm:rounded-2xl shadow-lg border border-white/60 dark:border-slate-700/60">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center flex-shrink-0">
                  <Shield className="h-5 w-5 text-[#69adba]" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Identity Verification
                </h3>
              </div>
              <p className="mt-3 text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                Secure identity verification with AI-powered document processing and compliance checks.
              </p>
            </div>

            <div className="bg-white dark:bg-slate-800 p-5 sm:p-6 rounded-xl sm:rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center flex-shrink-0">
                  <FileCheck className="h-5 w-5 text-[#69adba]" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Document Management
                </h3>
              </div>
              <p className="mt-3 text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                Upload, manage, and organize all your exchange documents with intelligent categorization.
              </p>
            </div>

            <div className="bg-white dark:bg-slate-800 p-5 sm:p-6 rounded-xl sm:rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center flex-shrink-0">
                  <Calculator className="h-5 w-5 text-[#69adba]" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Capital Gains Calculator
                </h3>
              </div>
              <p className="mt-3 text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                Calculate capital gains, replacement property requirements, and tax implications.
              </p>
            </div>

            <div className="bg-white dark:bg-slate-800 p-5 sm:p-6 rounded-xl sm:rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-5 w-5 text-[#69adba]" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
                  Property Marketplace
                </h3>
              </div>
              <p className="mt-3 text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                Browse qualified DSTs and direct properties from institutional sponsors like ARES, JLL, and Inland.
              </p>
            </div>

            <div className="bg-white dark:bg-slate-800 p-5 sm:p-6 rounded-xl sm:rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center flex-shrink-0">
                  <FileCheck className="h-5 w-5 text-[#69adba]" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
                  IRS Forms Filing
                </h3>
              </div>
              <p className="mt-3 text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                Automated IRS form generation and filing with built-in compliance checks.
              </p>
            </div>

            <div className="bg-white dark:bg-slate-800 p-5 sm:p-6 rounded-xl sm:rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
              <div className="flex items-center space-x-3">
                <div className="h-10 w-10 rounded-lg bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center flex-shrink-0">
                  <Users className="h-5 w-5 text-[#69adba]" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
                  QI & RIA Services
                </h3>
              </div>
              <p className="mt-3 text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                Qualified Intermediary and Registered Investment Advisor services in one unified platform.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-slate-50 dark:bg-slate-800/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                Why Choose Open1031?
              </h2>
              <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
                Our platform combines cutting-edge technology with deep domain expertise to deliver 
                the most comprehensive 1031 exchange solution available.
              </p>
              
              <div className="mt-8 space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">AI-Powered Workflows</h3>
                    <p className="text-slate-600 dark:text-slate-400">Automated processes reduce errors and save time</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">Expert Domain Logic</h3>
                    <p className="text-slate-600 dark:text-slate-400">Built by 1031 exchange professionals</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">Seamless Experience</h3>
                    <p className="text-slate-600 dark:text-slate-400">Intuitive interface designed for all skill levels</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-slate-100">Full Compliance</h3>
                    <p className="text-slate-600 dark:text-slate-400">Built-in regulatory compliance and audit trails</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-[#69adba]/10 to-[#69adba]/20 dark:from-[#69adba]/20 dark:to-[#69adba]/30 p-8 rounded-2xl border border-[#69adba]/20 dark:border-[#69adba]/30">
              <div className="text-center">
                <Clock className="h-16 w-16 text-[#69adba] mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-2">
                  Save 80% of Your Time
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Traditional 1031 exchanges can take months. Our platform reduces the process 
                  to weeks while ensuring full compliance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security & Compliance Section */}
      <section id="security" className="py-12 sm:py-16 md:py-20 relative z-10 scroll-mt-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12 md:mb-16">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 bg-[#69adba]/10 dark:bg-[#69adba]/20 rounded-full mb-3 sm:mb-4">
              <Shield className="h-6 w-6 sm:h-8 sm:w-8 text-[#69adba]" />
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100 mb-3 sm:mb-4 px-4 sm:px-0">
              Security & Compliance
            </h2>
            <p className="mt-3 sm:mt-4 text-base sm:text-lg text-slate-600 dark:text-slate-400 max-w-3xl mx-auto px-4 sm:px-0">
              Your data security and privacy are our top priorities. We employ industry-leading security measures 
              to protect your information.
            </p>
          </div>

          {/* Security Features */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-10 sm:mb-12 md:mb-16">
            {/* Encryption */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4">
                <Lock className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                End-to-End Encryption
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                All data is encrypted in transit (TLS 1.3) and at rest (AES-256) to ensure maximum security.
              </p>
            </div>

            {/* Access Controls */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4">
                <Eye className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Access Controls
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Role-based access with multi-factor authentication and regular security audits.
              </p>
            </div>

            {/* SOC 2 */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4">
                <FileCheck className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                SOC 2 Certified
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Our infrastructure is SOC 2 Type II certified, ensuring the highest standards of security and compliance.
              </p>
            </div>

            {/* Regular Audits */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Regular Audits
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Third-party security audits and penetration testing ensure continuous protection.
              </p>
            </div>

            {/* Monitoring */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4">
                <Shield className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                24/7 Monitoring
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Continuous security monitoring and rapid incident response to protect your data.
              </p>
            </div>

            {/* Data Privacy */}
            <div className="bg-white dark:bg-slate-800 p-8 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700">
              <div className="h-12 w-12 rounded-xl bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center mb-4">
                <Lock className="h-6 w-6 text-[#69adba]" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Data Privacy
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                We never sell your data. Your information is used solely to provide our services.
              </p>
            </div>
          </div>

          {/* Compliance Section */}
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100 text-center mb-8">
              Compliance & Certifications
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-slate-800 p-6 rounded-xl">
                <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">SOC 2 Type II</h4>
                <p className="text-slate-600 dark:text-slate-400">
                  Certified for security, availability, processing integrity, confidentiality, and privacy.
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 p-6 rounded-xl">
                <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">IRS Compliance</h4>
                <p className="text-slate-600 dark:text-slate-400">
                  Full compliance with IRS regulations for 1031 exchanges and qualified intermediary services.
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 p-6 rounded-xl">
                <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">GDPR Ready</h4>
                <p className="text-slate-600 dark:text-slate-400">
                  Compliant with GDPR and other international data protection regulations.
                </p>
              </div>
              <div className="bg-white dark:bg-slate-800 p-6 rounded-xl">
                <h4 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">KYC/AML</h4>
                <p className="text-slate-600 dark:text-slate-400">
                  Know Your Customer and Anti-Money Laundering compliance for all transactions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-[#69adba] dark:bg-[#5a9aa6]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-white">
            Ready to Start Your 1031 Exchange?
          </h2>
          <p className="mt-3 sm:mt-4 text-base sm:text-lg text-white/90 px-4 sm:px-0">
            Join thousands of investors who trust Open1031 for their exchange needs.
          </p>
          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-4 sm:px-0">
            <button 
              onClick={() => setShowChat(true)}
              className="flex items-center justify-center space-x-2 px-12 py-5 text-lg font-bold text-white bg-white/20 hover:bg-white/30 rounded-xl shadow-2xl hover:shadow-white/30 hover:scale-105 active:scale-95 transition-all duration-200 border-2 border-white/40"
            >
              <BookOpen className="h-5 w-5" />
              <span>Learn More</span>
            </button>
            <button 
              onClick={handleStartExchange}
              className="flex items-center justify-center space-x-2 px-12 py-5 text-lg font-bold text-white bg-white/20 hover:bg-white/30 rounded-xl shadow-2xl hover:shadow-white/30 hover:scale-105 active:scale-95 transition-all duration-200 border-2 border-white/40"
            >
              <Building2 className="h-5 w-5" />
              <span>Start an Exchange</span>
            </button>
          </div>
        </div>
      </section>

      <PublicFooter />

      {/* Email Capture Modal */}
      <EmailCaptureModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onEmailCaptured={handleEmailCaptured}
      />

      {/* Registration Modal */}
      <RegisterModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSuccess={handleRegistrationSuccess}
        prefilledEmail={capturedEmail}
      />

      {/* Desktop: Docked Chat Panel (bottom-right) */}
      {showChat && !isMobile && (
        <div className="fixed bottom-6 right-6 z-[9999] w-[420px] max-w-[90vw] h-[600px] min-h-[420px] max-h-[85vh] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl overflow-hidden resize-y">
          <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-700 px-4 py-3 bg-white dark:bg-slate-800">
            <div className="flex items-center space-x-2">
              <Logo className="h-8 w-auto" />
            </div>
            <button
              onClick={() => setShowChat(false)}
              className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            </button>
          </div>
          <div className="h-[calc(100%-56px)]">
            <ChatInterface 
              onStartProcess={handleStartProcess}
              className="h-full"
            />
          </div>
        </div>
      )}

      {/* Floating Chat Button (when chat is not open) */}
      {!showChat && (
        <div className="fixed bottom-6 right-6 z-[9999]" style={{ backgroundColor: "transparent", background: "transparent", boxShadow: "none" }}>
          {/* Pulsing Ring Animation */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#69adba] to-[#5a9aa6] animate-ping opacity-20"></div>
          
          {/* Main Button */}
          <button
            onClick={handleStartExchange}
            className="relative group h-16 w-16 lg:h-18 lg:w-18 bg-gradient-to-br from-[#69adba] via-[#5fbdce] to-[#69adba] hover:from-[#5fbdce] hover:via-[#69adba] hover:to-[#5fbdce] text-white rounded-full shadow-[0_20px_60px_rgba(105,173,186,0.5),0_8px_24px_rgba(0,0,0,0.2),0_0_0_1px_rgba(255,255,255,0.1)_inset] hover:shadow-[0_24px_72px_rgba(105,173,186,0.6),0_12px_32px_rgba(0,0,0,0.25),0_0_0_1px_rgba(255,255,255,0.2)_inset] flex items-center justify-center hover:scale-110 active:scale-95 transition-all duration-500 ease-out"
            type="button"
          >
            {/* Glossy Shine Effect */}
            <div className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-0 transition-opacity"></div>
            
            {/* Icon with subtle animation */}
            <MessageCircle className="h-8 w-8 lg:h-9 lg:w-9 relative z-10 drop-shadow-lg group-hover:rotate-12 transition-transform duration-300" fill="none" style={{ backgroundColor: "transparent", background: "transparent", boxShadow: "none" }} />
          </button>
        </div>
      )}

    </div>
  )
}
